import { supabase } from '../lib/supabase'
import { getErrorMessage } from '../lib/errors'

const CHAT_CHANNEL_SELECT_FULL =
  'id, channel_type, job_id, conversation_id, phone_call_log_id, name, dm_pair_key, created_at'
const CHAT_CHANNEL_SELECT_BASIC =
  'id, channel_type, job_id, name, dm_pair_key, created_at'

/** True when DB lacks inbox link columns (migration 202609031300 not applied). */
function isMissingInboxChannelColumns(error: { message?: string; code?: string } | null): boolean {
  if (!error?.message) return false
  const msg = error.message.toLowerCase()
  const mentionsCol =
    msg.includes('conversation_id') || msg.includes('phone_call_log_id')
  if (!mentionsCol) return false
  return (
    error.code === '42703' ||
    msg.includes('does not exist') ||
    msg.includes('could not find')
  )
}

/** True when DB lacks mentioned_user_ids (migration 202609031400 not applied). */
function isMissingMentionedUserIdsColumn(error: { message?: string; code?: string } | null): boolean {
  if (!error?.message) return false
  const msg = error.message.toLowerCase()
  if (!msg.includes('mentioned_user_ids')) return false
  return (
    error.code === '42703' ||
    msg.includes('does not exist') ||
    msg.includes('could not find')
  )
}

export interface ChatChannel {
  id: string
  channel_type: 'general' | 'job' | 'direct' | 'inbox'
  job_id?: string | null
  conversation_id?: string | null
  phone_call_log_id?: string | null
  name?: string | null
  dm_pair_key?: string | null
  created_at?: string
}

export interface ChatMessage {
  id: string
  channel_id: string
  sender_id: string
  body: string
  attachment_url?: string | null
  mentioned_user_ids?: string[] | null
  created_at: string
  sender?: {
    id: string
    first_name?: string | null
    last_name?: string | null
    email?: string | null
  } | null
}

export interface ChatDirectoryUser {
  id: string
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  role?: string | null
}

export interface ChatChannelListItem extends ChatChannel {
  label: string
  subtitle?: string | null
  job_number?: string | null
  member_names?: string[]
}

function displayName(u?: {
  first_name?: string | null
  last_name?: string | null
  email?: string | null
} | null): string {
  if (!u) return 'Unknown'
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim()
  return name || u.email || 'Unknown'
}

export function chatDirectoryDisplayName(u?: {
  first_name?: string | null
  last_name?: string | null
  email?: string | null
} | null): string {
  return displayName(u)
}

export async function fetchGeneralChannel(): Promise<ChatChannel | null> {
  const { data, error } = await supabase
    .from('chat_channels')
    .select('id, channel_type, job_id, name, dm_pair_key, created_at')
    .eq('channel_type', 'general')
    .is('job_id', null)
    .maybeSingle()
  if (error) throw error
  return data
}

export async function ensureJobChatChannel(jobId: string): Promise<string> {
  const { data, error } = await supabase.rpc('ensure_job_chat_channel', { p_job_id: jobId })
  if (error) throw error
  return data as string
}

export async function ensureInboxChatChannel(conversationId: string): Promise<string> {
  const { data, error } = await supabase.rpc('ensure_inbox_chat_channel', {
    p_conversation_id: conversationId,
  })
  if (error) throw new Error(getErrorMessage(error, 'Failed to open team discuss'))
  return data as string
}

export async function ensureInboxCallChatChannel(phoneCallLogId: string): Promise<string> {
  const { data, error } = await supabase.rpc('ensure_inbox_call_chat_channel', {
    p_phone_call_log_id: phoneCallLogId,
  })
  if (error) throw new Error(getErrorMessage(error, 'Failed to open team discuss'))
  return data as string
}

export async function ensureDirectChatChannel(otherUserId: string): Promise<string> {
  const { data, error } = await supabase.rpc('ensure_direct_chat_channel', {
    p_other_user_id: otherUserId,
  })
  if (error) throw error
  return data as string
}

export async function fetchChatDirectoryUsers(): Promise<ChatDirectoryUser[]> {
  const { data: session } = await supabase.auth.getUser()
  const me = session.user?.id
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, first_name, last_name, email, role')
    .eq('is_active', true)
    .neq('role', 'customer')
    .order('first_name', { ascending: true })
  if (error) throw error
  return (data || []).filter((u) => u.id !== me)
}

export async function fetchChatChannels(): Promise<ChatChannelListItem[]> {
  const { data: session } = await supabase.auth.getUser()
  const me = session.user?.id

  // Prefer full select (inbox columns). Soft-fallback if migration 202609031300 not applied.
  let channels: ChatChannel[] | null = null
  let error: { message?: string; code?: string; details?: string | null; hint?: string | null } | null =
    null

  const full = await supabase
    .from('chat_channels')
    .select(CHAT_CHANNEL_SELECT_FULL)
    .order('created_at', { ascending: true })
  if (full.error && isMissingInboxChannelColumns(full.error)) {
    const fallback = await supabase
      .from('chat_channels')
      .select(CHAT_CHANNEL_SELECT_BASIC)
      .order('created_at', { ascending: true })
    channels = (fallback.data || null) as ChatChannel[] | null
    error = fallback.error
  } else {
    channels = (full.data || null) as ChatChannel[] | null
    error = full.error
  }
  if (error) throw new Error(getErrorMessage(error, 'Failed to load chat channels'))
  if (!channels?.length) return []

  const jobIds = channels.map((c) => c.job_id).filter(Boolean) as string[]
  const jobsById = new Map<string, { job_number?: string | null; title?: string | null }>()
  if (jobIds.length) {
    const { data: jobs } = await supabase
      .from('jobs')
      .select('id, job_number, title')
      .in('id', jobIds)
    for (const j of jobs || []) jobsById.set(j.id, j)
  }

  const convoIds = channels
    .filter((c) => c.channel_type === 'inbox' && c.conversation_id)
    .map((c) => c.conversation_id) as string[]
  const convosById = new Map<
    string,
    { channel?: string | null; contact_name?: string | null; contact_info?: string | null }
  >()
  if (convoIds.length) {
    const { data: convos } = await supabase
      .from('conversations')
      .select('id, channel, contact_name, contact_info')
      .in('id', convoIds)
    for (const c of convos || []) convosById.set(c.id, c)
  }

  const callIds = channels
    .filter((c) => c.channel_type === 'inbox' && c.phone_call_log_id)
    .map((c) => c.phone_call_log_id) as string[]
  const callsById = new Map<string, { caller_name?: string | null; phone_number?: string | null }>()
  if (callIds.length) {
    const { data: calls } = await supabase
      .from('phone_call_logs')
      .select('id, caller_name, phone_number')
      .in('id', callIds)
    for (const c of calls || []) callsById.set(c.id, c)
  }

  const directIds = channels.filter((c) => c.channel_type === 'direct').map((c) => c.id)
  const membersByChannel = new Map<string, string[]>()
  if (directIds.length) {
    const { data: members } = await supabase
      .from('chat_channel_members')
      .select('channel_id, user_id')
      .in('channel_id', directIds)
    const userIds = Array.from(new Set((members || []).map((m) => m.user_id)))
    const profilesById = new Map<string, ChatDirectoryUser>()
    if (userIds.length) {
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email, role')
        .in('id', userIds)
      for (const p of profiles || []) profilesById.set(p.id, p)
    }
    for (const m of members || []) {
      const list = membersByChannel.get(m.channel_id) || []
      const profile = profilesById.get(m.user_id)
      if (m.user_id !== me) list.push(displayName(profile))
      else if (!list.length) list.push('You')
      membersByChannel.set(m.channel_id, list)
    }
  }

  return channels.map((c) => {
    if (c.channel_type === 'general') {
      return { ...c, label: c.name || 'Company General', subtitle: 'Everyone' }
    }
    if (c.channel_type === 'job') {
      const job = c.job_id ? jobsById.get(c.job_id) : undefined
      return {
        ...c,
        label: job?.job_number ? `Job ${job.job_number}` : c.name || 'Job chat',
        subtitle: job?.title || null,
        job_number: job?.job_number || null,
      }
    }
    if (c.channel_type === 'inbox') {
      if (c.conversation_id) {
        const convo = convosById.get(c.conversation_id)
        const contact = convo?.contact_name || convo?.contact_info || 'Contact'
        const ch = (convo?.channel || 'sms').toLowerCase()
        const prefix = ch === 'email' ? 'Email' : ch === 'whatsapp' ? 'WhatsApp' : 'SMS'
        return {
          ...c,
          label: c.name || `${prefix} · ${contact}`,
          subtitle: 'Inbox discussion',
        }
      }
      if (c.phone_call_log_id) {
        const call = callsById.get(c.phone_call_log_id)
        const contact = call?.caller_name || call?.phone_number || 'Call'
        return {
          ...c,
          label: c.name || `Call · ${contact}`,
          subtitle: 'Inbox discussion',
        }
      }
      return { ...c, label: c.name || 'Inbox discussion', subtitle: 'Inbox discussion' }
    }
    const names = membersByChannel.get(c.id) || []
    const otherNames = names.filter((n) => n !== 'You')
    return {
      ...c,
      label: otherNames.length ? otherNames.join(', ') : 'Direct message',
      subtitle: 'Direct',
      member_names: names,
    }
  })
}

export async function fetchChatMessages(channelId: string): Promise<ChatMessage[]> {
  const fullSelect =
    'id, channel_id, sender_id, body, attachment_url, mentioned_user_ids, created_at'
  const basicSelect = 'id, channel_id, sender_id, body, attachment_url, created_at'

  let rows: ChatMessage[] = []
  const full = await supabase
    .from('chat_messages')
    .select(fullSelect)
    .eq('channel_id', channelId)
    .order('created_at', { ascending: true })
    .limit(200)
  if (full.error && isMissingMentionedUserIdsColumn(full.error)) {
    const fallback = await supabase
      .from('chat_messages')
      .select(basicSelect)
      .eq('channel_id', channelId)
      .order('created_at', { ascending: true })
      .limit(200)
    if (fallback.error) throw fallback.error
    rows = (fallback.data || []) as ChatMessage[]
  } else if (full.error) {
    throw full.error
  } else {
    rows = (full.data || []) as ChatMessage[]
  }

  const senderIds = Array.from(new Set(rows.map((r) => r.sender_id)))
  const senders = new Map<string, ChatDirectoryUser>()
  if (senderIds.length) {
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, first_name, last_name, email, role')
      .in('id', senderIds)
    for (const p of profiles || []) senders.set(p.id, p)
  }
  return rows.map((r) => ({
    ...r,
    sender: senders.get(r.sender_id) || null,
  }))
}

export const CHAT_ATTACHMENT_ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,video/mp4,video/quicktime,application/pdf'

export function isChatImageUrl(url: string): boolean {
  if (/\.(mp4|mov|quicktime|pdf)(\?|$)/i.test(url)) return false
  return /\.(jpe?g|png|gif|webp|heic|heif)(\?|$)/i.test(url) || /\/chat-attachments\//i.test(url)
}

export function isChatVideoUrl(url: string): boolean {
  return /\.(mp4|mov|quicktime)(\?|$)/i.test(url)
}

export async function uploadChatAttachment(channelId: string, file: File): Promise<string> {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80) || 'file'
  const storagePath = `${channelId}/${Date.now()}_${safeName}`
  const { error: uploadError } = await supabase.storage
    .from('chat-attachments')
    .upload(storagePath, file, {
      contentType: file.type || undefined,
      upsert: false,
    })
  if (uploadError) throw uploadError
  const { data } = supabase.storage.from('chat-attachments').getPublicUrl(storagePath)
  return data.publicUrl
}

export async function sendChatMessage(
  channelId: string,
  body: string,
  attachmentUrl?: string | null,
  mentionedUserIds?: string[] | null
): Promise<void> {
  const { data: session } = await supabase.auth.getUser()
  const uid = session.user?.id
  if (!uid) throw new Error('Not signed in')
  const trimmed = body.trim()
  if (!trimmed && !attachmentUrl) throw new Error('Message is empty')

  const mentions = Array.from(
    new Set((mentionedUserIds || []).filter((id) => Boolean(id) && id !== uid))
  )

  const base = {
    channel_id: channelId,
    sender_id: uid,
    body: trimmed || '',
    attachment_url: attachmentUrl || null,
  }

  if (mentions.length === 0) {
    const { error } = await supabase.from('chat_messages').insert(base)
    if (error) throw error
    return
  }

  const withMentions = { ...base, mentioned_user_ids: mentions }
  const { error } = await supabase.from('chat_messages').insert(withMentions)
  if (error && isMissingMentionedUserIdsColumn(error)) {
    // Soft-fallback: send without structured mentions if migration not applied
    const { error: fallbackError } = await supabase.from('chat_messages').insert(base)
    if (fallbackError) throw fallbackError
    return
  }
  if (error) throw error
}

export interface PropertyJobChatItem {
  job_id: string
  job_number: string | null
  title: string | null
  status: string | null
  channel_id: string | null
  last_message_at: string | null
  last_message_preview: string | null
  has_attachment: boolean
}

/** List jobs for a property with their job-chat channel and last message (if any). */
export async function fetchPropertyJobChats(propertyId: string): Promise<PropertyJobChatItem[]> {
  const { data: jobs, error: jobsError } = await supabase
    .from('jobs')
    .select('id, job_number, title, status, created_at')
    .eq('property_id', propertyId)
    .order('created_at', { ascending: false })
  if (jobsError) throw jobsError
  if (!jobs?.length) return []

  const jobIds = jobs.map((j) => j.id)
  const { data: channels, error: chError } = await supabase
    .from('chat_channels')
    .select('id, job_id')
    .eq('channel_type', 'job')
    .in('job_id', jobIds)
  if (chError) throw chError

  const channelByJob = new Map<string, string>()
  for (const c of channels || []) {
    if (c.job_id) channelByJob.set(c.job_id, c.id)
  }

  const channelIds = Array.from(channelByJob.values())
  const lastByChannel = new Map<
    string,
    { body: string; attachment_url: string | null; created_at: string }
  >()
  if (channelIds.length) {
    await Promise.all(
      channelIds.map(async (channelId) => {
        const { data: msgs } = await supabase
          .from('chat_messages')
          .select('channel_id, body, attachment_url, created_at')
          .eq('channel_id', channelId)
          .order('created_at', { ascending: false })
          .limit(1)
        const m = msgs?.[0]
        if (m) {
          lastByChannel.set(channelId, {
            body: m.body || '',
            attachment_url: m.attachment_url || null,
            created_at: m.created_at,
          })
        }
      })
    )
  }

  return jobs.map((j) => {
    const channelId = channelByJob.get(j.id) || null
    const last = channelId ? lastByChannel.get(channelId) : undefined
    const preview =
      last?.body?.trim() ||
      (last?.attachment_url ? 'Attachment' : null)
    return {
      job_id: j.id,
      job_number: j.job_number || null,
      title: j.title || null,
      status: j.status || null,
      channel_id: channelId,
      last_message_at: last?.created_at || null,
      last_message_preview: preview,
      has_attachment: Boolean(last?.attachment_url),
    }
  })
}

export async function markChannelRead(channelId: string): Promise<void> {
  const { data: session } = await supabase.auth.getUser()
  const uid = session.user?.id
  if (!uid) return
  const { error } = await supabase.from('chat_channel_reads').upsert({
    channel_id: channelId,
    user_id: uid,
    last_read_at: new Date().toISOString(),
  })
  if (error) throw error
}

export function chatSenderLabel(message: ChatMessage): string {
  return displayName(message.sender)
}
